/**
 * 전역(레이아웃 공통) 초기화.
 * - public(PICHI): Lenis, AOS, 드로어, 스크롤 모션, 탑버튼, 자료요청 팝업 등.
 * - admin: 관리자 페이지 폼/네비/리스트 초기화.
 * PHP 라우트(page TS)는 PageApplication 이 담당.
 */
import { Toast, showFlashToasts } from "../utils/Toast";
import { Modal } from "../utils/Modal";

import { AosInitializer } from "../home/global/AosInitializer";
import { ScrollRevealController } from "../home/global/ScrollRevealController";
import { ParallaxImageController } from "../home/global/ParallaxImageController";
import { ClipPathRevealController } from "../home/global/ClipPathRevealController";
import { SplitCharsService } from "../home/global/SplitCharsService";
import { SubVisualTitleSplitController } from "../home/global/SubVisualTitleSplitController";
import { SmoothScrollManager } from "../home/global/SmoothScrollManager";
import { DrawerController } from "../home/global/DrawerController";
import { TopButtonController } from "../home/global/TopButtonController";
import { AssetRequestPopupController } from "../home/global/AssetRequestPopupController";
import { SitePopupController } from "../home/global/SitePopupController";

import {
    initAdminForms,
    initAdminDateTimeInputs,
    initAdminFlatpickrPickers,
    initAdminSelects,
    initTagInputs,
    initNumberSteppers,
    initCharCounters,
    initAdminSearchBars,
    initAdminQuillEditors,
    initIntlPhoneInputs,
    initCountrySelects,
    initFileUploaders,
    initAdminLayoutDrawer,
    initAdminNotificationsDropdown,
    initAdminNotificationsUnreadPoll,
    initAutoSubmitSelects,
    initAdminUserDestroyButtons,
    initBulkSelect,
    initPageMove,
    initAdminThemeToggle,
    initThumbPreview,
    initAdminPasswordToggle,
} from "../admin/global";
import { initOpsConsole, initOpsTabs } from "../admin/pages/OpsConsole";
import { ListingTable } from "../admin/components/ListingTable";
import { CardTable } from "../admin/components/CardTable";
import type { ControllerMap } from "./ControllerRegistry";

export class GlobalApplication {
    constructor(
        private readonly registry: Record<string, ControllerMap>,
    ) {
        // 등록맵은 PageApplication 에서 직접 사용. 여기선 보관만.
        void this.registry;
    }

    run(): void {
        this.exposeToastModal();
        this.initPagination();
        if (document.body.classList.contains("admin")) {
            this.runAdmin();
        } else {
            this.runPublic();
        }
    }

    private exposeToastModal(): void {
        const w = window as unknown as { Toast: typeof Toast; Modal: typeof Modal };
        w.Toast = Toast;
        w.Modal = Modal;
    }

    /** 접근성: prefers-reduced-motion: reduce + 레이아웃 data-rm-aware 플래그가 모두 켜진 경우 JS 모션을 줄인다. */
    private prefersReducedMotion(): boolean {
        if (document.documentElement.getAttribute("data-rm-aware") !== "1") return false;
        return (
            typeof window.matchMedia === "function" &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches
        );
    }

    private initPagination(): void {
        document.querySelectorAll(".no-pagination a[data-page]").forEach((link) => {
            link.addEventListener("click", (e) => {
                const page = (e.currentTarget as HTMLElement).getAttribute("data-page");
                if (!page || (e.currentTarget as HTMLAnchorElement).hasAttribute("disabled")) {
                    e.preventDefault();
                    return;
                }
                e.preventDefault();
                const urlParams = new URLSearchParams(window.location.search);
                urlParams.set("page", page);
                window.location.href = window.location.pathname + "?" + urlParams.toString();
            });
        });
    }

    private runAdmin(): void {
        showFlashToasts();
        initAdminForms();
        initAdminPasswordToggle();
        initAdminDateTimeInputs();
        initAdminFlatpickrPickers();
        initAdminSelects();
        initTagInputs();
        initNumberSteppers();
        initCharCounters();
        // 동적으로 추가되는 폼 요소(예: 세트구성 "+ 메뉴 추가")에서 호출할 수 있도록 노출
        (window as unknown as { AdminUI: Record<string, unknown> }).AdminUI = {
            initAdminSelects,
            initNumberSteppers,
            initTagInputs,
            initCharCounters,
        };
        initAdminSearchBars();
        initAdminQuillEditors();
        initIntlPhoneInputs();
        initCountrySelects();
        initFileUploaders();
        initAdminLayoutDrawer();
        initAdminNotificationsDropdown();
        initAdminNotificationsUnreadPoll();
        initAutoSubmitSelects();
        initAdminUserDestroyButtons();
        initBulkSelect();
        initPageMove();
        initAdminThemeToggle();
        initOpsConsole();
        initOpsTabs();
        ListingTable.initAll();
        CardTable.initAll();
        initThumbPreview();
    }

    private runPublic(): void {
        SitePopupController.init();
        const reduceMotion = this.prefersReducedMotion();

        AosInitializer.init();
        ScrollRevealController.initFadeUp();
        ScrollRevealController.initBlur();
        ScrollRevealController.initListUp();
        ParallaxImageController.init();
        ClipPathRevealController.init();
        SplitCharsService.expose();
        SubVisualTitleSplitController.init();
        if (!reduceMotion) SmoothScrollManager.init();
        DrawerController.init();
        TopButtonController.init();
        AssetRequestPopupController.init();
    }
}
